/**
 * Toast (U5): ToastProvider + useToast(). Glass notification stack,
 * bottom-right, tone accent bar, auto-dismiss.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type JSX,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { cx } from './cx';
import { IconAlert, IconCheck, IconClose, IconInfo } from './icons';

export type ToastTone = 'success' | 'danger' | 'warning' | 'info';

export interface ToastOptions {
  title: ReactNode;
  description?: ReactNode;
  tone?: ToastTone;
  /** ms; default 4500. Pass 0 to keep until dismissed. */
  duration?: number;
}

interface ToastItem extends ToastOptions {
  id: number;
}

interface ToastApi {
  toast: (options: ToastOptions) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error('useToast must be used inside a <ToastProvider>');
  return api;
}

function ToneIcon({ tone }: { tone: ToastTone }): JSX.Element {
  if (tone === 'success') return <IconCheck size={13} />;
  if (tone === 'danger' || tone === 'warning') return <IconAlert size={13} />;
  return <IconInfo size={13} />;
}

export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [items, setItems] = useState<ToastItem[]>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const toast = useCallback(
    (options: ToastOptions) => {
      const id = nextId.current;
      nextId.current += 1;
      setItems((prev) => [...prev.slice(-4), { ...options, id }]);
      const duration = options.duration ?? 4500;
      if (duration > 0) {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), duration),
        );
      }
      return id;
    },
    [dismiss],
  );

  const api = useMemo(() => ({ toast, dismiss }), [toast, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div className="bl-toasts" role="region" aria-label="Notifications" aria-live="polite">
          {items.map((item) => {
            const tone = item.tone ?? 'info';
            return (
              <div key={item.id} className={cx('bl-toast', `bl-toast--${tone}`)} role="status">
                <span className="bl-toast__icon">
                  <ToneIcon tone={tone} />
                </span>
                <div className="bl-toast__content">
                  <div className="bl-toast__title">{item.title}</div>
                  {item.description != null && (
                    <div className="bl-toast__desc">{item.description}</div>
                  )}
                </div>
                <button
                  type="button"
                  className="bl-toast__close"
                  aria-label="Dismiss notification"
                  onClick={() => dismiss(item.id)}
                >
                  <IconClose size={13} />
                </button>
              </div>
            );
          })}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}
